import React, {
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { Anchor, ConfigProvider, Tabs } from '@bedrock/components';
import classNames from 'classnames';
import {
  useDebounceFn,
  useThrottleFn,
  useFullscreen,
  useMemoizedFn,
  useUpdate,
} from 'ahooks';
import './index.less';
import { isEqual } from 'lodash';

const cx = classNames;

const Link = Anchor.Link;

let tabAnchorId = 0;

//
type TabsAnchorProps = {
  children: React.ReactNode;
  // 推导出Tabs的属性
  tabsProps?: Omit<React.ComponentProps<typeof Tabs>, 'activeKey'>; // tabs的属性
  contentWrapperProps?: React.HTMLAttributes<HTMLDivElement>; // 内容区域的属性
  getAnchorHeader?: (wrapperDom: Element) => Element[]; // 获取锚点头部的方法
  renderHeaderTab?: (item: Element, index: number) => React.ReactElement; // tabPane的渲染方法
  offsetTop?: number; // 锚点距离顶部的距离
  getAnchorScrollContainer?: () => HTMLElement; // Anchor 监听的滚动容器（默认内容区 wrapper，Drawer 等非自身滚动场景需传入实际滚动容器）
} & React.HTMLAttributes<HTMLDivElement>;

const TabsAnchor = (props: TabsAnchorProps, ref) => {
  const {
    children,
    getAnchorHeader = (wrapperDom) =>
      Array.from(wrapperDom.querySelectorAll('[data-is-anchor]')),
    tabsProps,
    contentWrapperProps,
    className,
    offsetTop,
    getAnchorScrollContainer,
    ...resProps
  } = props;
  const idRef = useRef<number>(++tabAnchorId);
  const wrapperRef = React.useRef<HTMLDivElement>();
  const anchorRef = React.useRef<any>();
  const [selectedId, setSelectedId] = useState<string>(
    `#tab-${idRef.current}-0`
  );
  const [headerList, setHeaderList] = useState<Element[]>([]);
  // 程序化点击意图标记：tab 点击后记录点击的 href，供滚动监听底部兜底区分
  // "用户点击的 tab"与"纯用户滚动到底"，避免点击中间 tab 被钳制到底时误切到最后一个
  const pendingClickRef = useRef<string | null>(null);
  const update = useUpdate();
  // 吸顶 tabs 高度：tabs 自身 sticky 吸顶时，Anchor 的 offsetTop 需叠加 tabs 高度，
  // 使点击跳转/激活判断避开吸顶遮挡（bedrock Anchor offsetTop = 目标距容器顶偏移）
  const [stickyTabsOffset, setStickyTabsOffset] = useState(0);

  const { run: setHeaderListDebounce, cancel: cancelSetHeaderList } =
    useThrottleFn(
      () => {
        if (!wrapperRef.current) return;
        const newHeaderList = getAnchorHeader(wrapperRef.current).map(
          (item: any, index) => {
            item.id = `tab-${idRef.current}-${index}`;
            return item;
          }
        );
        if (newHeaderList.length && !isEqual(newHeaderList, headerList)) {
          setHeaderList(newHeaderList);
        }
        update();
      },
      {
        wait: 500,
      }
    );

  useImperativeHandle(
    ref,
    () => ({
      setActiveTab: (activeKey: string) => {
        setSelectedId(activeKey);
        anchorRef.current?.setActiveAnchor?.(activeKey, true);
      },
    }),
    []
  );

  // 测量吸顶 tabs 高度（ResizeObserver 跟随高度变化）：tabs sticky 吸顶时叠加到 Anchor offsetTop，
  // 非吸顶回落 0（保持原 offsetTop 行为）
  useEffect(() => {
    const tabsEl = wrapperRef.current
      ?.closest('.mpc-tabs-Anchor')
      ?.querySelector<HTMLElement>('.mpc-tabs-Anchor-tabs');
    if (!tabsEl) return;
    const measure = () => {
      const isSticky = window.getComputedStyle(tabsEl).position === 'sticky';
      setStickyTabsOffset(isSticky ? tabsEl.offsetHeight : 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(tabsEl);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setHeaderListDebounce();
    let observer: MutationObserver;
    if (wrapperRef?.current) {
      // 监听wrapperRef的dom变化，获取所有 is-anchor-header
      observer = new MutationObserver((mutations) => {
        if (!mutations.length) return;
        setHeaderListDebounce();
      });
      observer.observe(wrapperRef.current, {
        childList: true,
        subtree: true,
      });
    }
    return () => {
      cancelSetHeaderList?.();
      observer?.disconnect?.();
    };
  }, []);
  return (
    <ConfigProvider getPopupContainer={() => wrapperRef?.current}>
      <div {...resProps} className={cx('mpc-tabs-Anchor', className)}>
        <Tabs
          {...tabsProps}
          className={cx('mpc-tabs-Anchor-tabs', tabsProps?.className)}
          onChange={(value: string) => {
            tabsProps?.onChange?.(value);
            // 记录程序化滚动意图：setActiveAnchor 触发的滚动后，滚动监听可能因点击区块
            // 无法滚进 active 判定区（如最后区块/短区块后被钳制到底）而回退选中其他区块，
            // 需据此区分"用户点击的 tab"与"纯用户滚动到底"，保留用户点击意图
            pendingClickRef.current = value;
            setSelectedId(value);
            anchorRef.current?.setActiveAnchor?.(value, true);
            // 程序化滚动为一次性（setActiveAnchor 内部 scrollRaf duration=1 单帧），
            // 双帧后清除标记，恢复正常滚动监听跟随（避免残留覆盖后续用户手动滚动）
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                if (pendingClickRef.current === value)
                  pendingClickRef.current = null;
              });
            });
          }}
          activeKey={selectedId}
        >
          {headerList.map((item, index) => {
            return (
              <Tabs.TabPane
                tab={props.renderHeaderTab?.(item, index) || item.textContent}
                key={`#tab-${idRef.current}-${index}`}
              />
            );
          })}
          {!headerList?.length && <Tabs.TabPane tab={' '} key={`loading`} />}
        </Tabs>

        <div
          {...contentWrapperProps}
          ref={wrapperRef}
          className={cx(
            'mpc-anchor-content-wrapper',
            contentWrapperProps?.className
          )}
        >
          {children}
        </div>
      </div>
      <Anchor
        // 能扔多远扔多远
        offsetTop={(offsetTop ?? 10) + stickyTabsOffset}
        style={{ display: 'none' }}
        useCaret={false}
        getScrollContainer={getAnchorScrollContainer}
        ref={anchorRef}
        onChange={(currentActiveLink: string) => {
          const scrollContainer = getAnchorScrollContainer?.();
          if (scrollContainer) {
            const maxScrollTop =
              scrollContainer.scrollHeight - scrollContainer.clientHeight;
            // 容差 1px 防 subpixel 渲染导致 scrollTop 与 maxScrollTop 差 1px 不触发
            if (scrollContainer.scrollTop >= maxScrollTop - 1) {
              // 滚动到底部兜底：bedrock Anchor 的滚动监听只把"区块顶进入容器顶
              // offsetTop+SCROLL_THRESHOLD(30px) 判定区"的区块判为 active。最后一个区块若高度不足
              // （无法滚进判定区，如法务合规报告区块其后无内容），滚动到底时会被回退选中更早区块，
              // 导致点击最后一个 tab 后 active 停留在前一区块（需二次点击才生效）。
              // 此处区分意图：
              // - 存在未完成的程序化点击（pendingClickRef）且点击的不是最后一个 → 点击区块被钳制
              //   到底无法对齐，保持用户点击的 tab（如点击"评估报告"时应高亮评估报告而非最后一个）
              // - 否则（纯用户滚动到底 / 点击的就是最后一个）→ 选中最后一个区块（antd 底部语义）
              const lastIndex = headerList.length - 1;
              if (lastIndex >= 0) {
                const lastHref = `#tab-${idRef.current}-${lastIndex}`;
                const pendingClick = pendingClickRef.current;
                setSelectedId(
                  pendingClick && pendingClick !== lastHref
                    ? pendingClick
                    : lastHref
                );
                return;
              }
            }
          }
          setSelectedId(currentActiveLink);
        }}
      >
        {headerList.map((item, index) => {
          return (
            <Link
              id={'#anchor' + `tab-${idRef.current}-${index}`}
              href={`#tab-${idRef.current}-${index}`}
            />
          );
        })}
      </Anchor>
    </ConfigProvider>
  );
};

export default forwardRef(TabsAnchor);
